import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import HttpService from "services/http.service";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import axios from "axios";
import countryOptions from "../../../layouts/ecommerce/products/new-product/components/List/Countries";

const WORLD_CLOCKS = [
  {
    city: "Istanbul",
    country: "Türkiye",
    timeZone: "Europe/Istanbul",
    code: "TR",
    latitude: 41.0082,
    longitude: 28.9784,
  },
  {
    city: "London",
    country: "United Kingdom",
    timeZone: "Europe/London",
    code: "GB",
    latitude: 51.5072,
    longitude: -0.1276,
  },
  {
    city: "New York",
    country: "United States",
    timeZone: "America/New_York",
    code: "US",
    latitude: 40.7128,
    longitude: -74.006,
  },
  {
    city: "Shanghai",
    country: "China",
    timeZone: "Asia/Shanghai",
    code: "CN",
    latitude: 31.2304,
    longitude: 121.4737,
  },
  {
    city: "Berlin",
    country: "Germany",
    timeZone: "Europe/Berlin",
    code: "DE",
    latitude: 52.52,
    longitude: 13.405,
  },
  {
    city: "Milano",
    country: "Italy",
    timeZone: "Europe/Rome",
    code: "IT",
    latitude: 45.4642,
    longitude: 9.19,
  },
  {
    city: "Madrid",
    country: "Spain",
    timeZone: "Europe/Madrid",
    code: "ES",
    latitude: 40.4168,
    longitude: -3.7038,
  },
  {
    city: "Brasilia",
    country: "Brazil",
    timeZone: "America/Sao_Paulo",
    code: "BR",
    latitude: -15.7939,
    longitude: -47.8828,
  },
];

const getWeatherMeta = (code) => {
  if ([0].includes(code)) return { icon: "wb_sunny", label: "Sunny" };
  if ([1, 2].includes(code)) return { icon: "partly_cloudy_day", label: "Partly Cloudy" };
  if ([3].includes(code)) return { icon: "cloud", label: "Cloudy" };
  if ([45, 48].includes(code)) return { icon: "foggy", label: "Foggy" };
  if ([51, 53, 55, 56, 57].includes(code)) return { icon: "grain", label: "Drizzle" };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { icon: "water_drop", label: "Rainy" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { icon: "ac_unit", label: "Snowy" };
  if ([95, 96, 99].includes(code)) return { icon: "thunderstorm", label: "Stormy" };

  return { icon: "device_thermostat", label: "Weather" };
};

// Hava durumu önbelleği (şehir koordinatı -> son veri); sayfa geçişlerinde dış servise tekrar gidilmez
const WEATHER_CACHE = new Map();
const WEATHER_CACHE_TTL_MS = 10 * 60 * 1000;

function DashboardNavbar({ absolute = false, light = false, isMini = false, breadcrumbTitle = null }) {
  const [navbarType, setNavbarType] = useState();

  const [usdToTry, setUsdToTry] = useState(null);
  const [eurToTry, setEurToTry] = useState(null);
  const [gbpToTry, setGbpToTry] = useState(null);

  const [usdTrend, setUsdTrend] = useState("same");
  const [eurTrend, setEurTrend] = useState("same");
  const [gbpTrend, setGbpTrend] = useState("same");

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedClock, setSelectedClock] = useState(WORLD_CLOCKS[0]);
  const [weather, setWeather] = useState(null);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Shipment notifications by mode: all / ocean / air / road.
  const [notificationMode, setNotificationMode] = useState("all");
  const notificationModeRef = useRef("all");
  const navigate = useNavigate();

  const oldUsdToTryRef = useRef(null);
  const oldEurToTryRef = useRef(null);
  const oldGbpToTryRef = useRef(null);

  const usdFlag = countryOptions.find((c) => c.code === "US")?.flagUrl;
  const eurFlag =
      countryOptions.find((c) => c.code === "EU")?.flagUrl ||
      "https://flagcdn.com/w40/eu.png";
  const gbpFlag = countryOptions.find((c) => c.code === "GB")?.flagUrl;

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;

  const [openMenu, setOpenMenu] = useState(null);
  const [worldClockAnchor, setWorldClockAnchor] = useState(null);

  const route = useLocation().pathname.split("/").slice(1);
  const isWorldClockOpen = Boolean(worldClockAnchor);

  const weatherMeta = getWeatherMeta(weather?.weathercode);

  const fetchWeather = async (city) => {
    // Navbar her sayfada yeniden oluşuyor; dış servise her geçişte gitmemek için kısa süreli önbellek
    const cacheKey = `${city.latitude},${city.longitude}`;
    const cached = WEATHER_CACHE.get(cacheKey);
    if (cached && Date.now() - cached.at < WEATHER_CACHE_TTL_MS) {
      setWeather(cached.data);
      return;
    }
    try {
      const response = await axios.get(
          `https://api.open-meteo.com/v1/forecast?latitude=${city.latitude}&longitude=${city.longitude}&current_weather=true`
      );

      const data = response.data?.current_weather || null;
      WEATHER_CACHE.set(cacheKey, { data, at: Date.now() });
      setWeather(data);
    } catch (error) {
      // Kritik olmayan gösterge: dış servis geçici olarak yanıt vermezse sessizce boş kal
      setWeather(cached?.data ?? null);
    }
  };

  useEffect(() => {
    fetchWeather(selectedClock);
  }, [selectedClock]);

  const fetchNotifications = async () => {
    try {
      // The badge always counts every notification; the list follows the mode filter.
      const response = await HttpService.get("/notifications");
      setUnreadCount(response.unreadCount || 0);
      const mode = notificationModeRef.current;
      if (mode === "all") {
        setNotifications(response.data || []);
      } else {
        const filtered = await HttpService.get(`/notifications?mode=${mode}`);
        if (notificationModeRef.current === mode) setNotifications(filtered.data || []);
      }
    } catch (error) {
      console.error("Bildirimler alınırken hata oluştu:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const getTrend = (current, previous) => {
    if (previous === null || current === undefined) return "same";
    if (current > previous) return "up";
    if (current < previous) return "down";
    return "same";
  };

  const getTrendColor = (trend) => {
    if (trend === "up") return "#16a34a";
    if (trend === "down") return "#dc2626";
    return darkMode ? "#94a3b8" : "#94a3b8";
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") return "arrow_drop_up";
    if (trend === "down") return "arrow_drop_down";
    return "remove";
  };

  const formatWorldTime = (timeZone) =>
      new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(currentTime);

  const fetchExchangeRates = async () => {
    try {
      const [usdResponse, eurResponse, gbpResponse] = await Promise.all([
        axios.get("https://api.exchangerate-api.com/v4/latest/USD"),
        axios.get("https://api.exchangerate-api.com/v4/latest/EUR"),
        axios.get("https://api.exchangerate-api.com/v4/latest/GBP"),
      ]);

      const usdRate = usdResponse.data?.rates?.TRY;
      const eurRate = eurResponse.data?.rates?.TRY;
      const gbpRate = gbpResponse.data?.rates?.TRY;

      setUsdTrend(getTrend(usdRate, oldUsdToTryRef.current));
      setEurTrend(getTrend(eurRate, oldEurToTryRef.current));
      setGbpTrend(getTrend(gbpRate, oldGbpToTryRef.current));

      oldUsdToTryRef.current = usdRate;
      oldEurToTryRef.current = eurRate;
      oldGbpToTryRef.current = gbpRate;

      setUsdToTry(usdRate);
      setEurToTry(eurRate);
      setGbpToTry(gbpRate);
    } catch (error) {
      console.error("Döviz kurları alınırken hata oluştu:", error);
    }
  };

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");
    fetchExchangeRates();

    const exchangeInterval = setInterval(fetchExchangeRates, 60000);
    return () => clearInterval(exchangeInterval);
  }, [fixedNavbar]);

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    function handleTransparentNavbar() {
      setTransparentNavbar(
          dispatch,
          (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleConfiguratorOpen = () =>
      setOpenConfigurator(dispatch, !openConfigurator);

  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(null);

  const handleNotificationClick = async (notification) => {
    handleCloseMenu();

    if (!notification.isRead) {
      setNotifications((prev) =>
        prev.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await HttpService.patch(`/notifications/${notification.id}/read`);
      } catch (error) {
        console.error("Bildirim okundu olarak işaretlenemedi:", error);
      }
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  const changeNotificationMode = (mode) => {
    notificationModeRef.current = mode;
    setNotificationMode(mode);
    fetchNotifications();
  };

  // Shipment notifications show the file's mode; their link names the file page.
  const notificationIcon = (notification) => {
    if (notification.type === "global-partner-application") return "person_add";
    const link = notification.link || "";
    if (link.startsWith("/shipping-order/create-ocean-shipment/")) return "directions_boat";
    if (link.startsWith("/shipping-order/create-air-shipment/")) return "flight";
    if (link.startsWith("/shipping-order/create-shipment/")) return "local_shipping";
    return "notifications";
  };

  const NOTIFICATION_MODES = [
    ["all", "All"],
    ["ocean", "Ocean"],
    ["air", "Air"],
    ["road", "Road"],
  ];

  const handleOpenWorldClock = (event) => {
    setWorldClockAnchor(event.currentTarget);
  };

  const handleCloseWorldClock = () => {
    setWorldClockAnchor(null);
  };

  const renderMenu = () => (
      <Menu
          anchorEl={openMenu}
          open={Boolean(openMenu)}
          onClose={handleCloseMenu}
          sx={{ mt: 2 }}
      >
        <MDBox display="flex" gap={0.5} px={1} pb={1} onKeyDown={(e) => e.stopPropagation()}>
          {NOTIFICATION_MODES.map(([mode, label]) => (
              <Chip
                  key={mode}
                  size="small"
                  label={label}
                  clickable
                  color={notificationMode === mode ? "info" : "default"}
                  variant={notificationMode === mode ? "filled" : "outlined"}
                  onClick={() => changeNotificationMode(mode)}
                  sx={{ fontSize: 12, color: notificationMode === mode ? "#fff !important" : undefined }}
              />
          ))}
        </MDBox>
        {notifications.length === 0 && (
            <NotificationItem
                icon={<Icon>notifications_none</Icon>}
                title={notificationMode === "all" ? "No notifications yet" : "No notifications for this mode"}
            />
        )}
        {notifications.map((notification) => (
            <NotificationItem
                key={notification.id}
                icon={<Icon>{notificationIcon(notification)}</Icon>}
                title={notification.title}
                onClick={() => handleNotificationClick(notification)}
                unread={!notification.isRead}
            />
        ))}
      </Menu>
  );

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const glassCardSx = {
    borderRadius: "14px",
    backdropFilter: "blur(16px)",
    background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.82)",
    border: darkMode
        ? "1px solid rgba(255,255,255,0.12)"
        : "1px solid rgba(226,232,240,0.95)",
    boxShadow: darkMode
        ? "0 8px 24px rgba(0,0,0,0.24)"
        : "0 8px 24px rgba(15,23,42,0.07)",
  };

  const renderCurrencyItem = ({ flag, code, value, trend }) => (
      <MDBox
          display="flex"
          alignItems="center"
          gap={0.8}
          px={1.25}
          py={0.75}
          sx={{
            ...glassCardSx,
            whiteSpace: "nowrap",
            minHeight: "34px",
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: darkMode
                  ? "0 10px 30px rgba(0,0,0,0.32)"
                  : "0 10px 30px rgba(15,23,42,0.11)",
            },
          }}
      >
        <img
            src={flag}
            alt={code}
            width={22}
            height={15}
            style={{ borderRadius: 3, objectFit: "cover" }}
        />

        <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{ color: darkMode ? "#fff" : "#334155" }}
        >
          {code}
        </MDTypography>

        <MDTypography
            variant="caption"
            fontWeight="medium"
            sx={{ color: darkMode ? "#d1d5db" : "#64748b" }}
        >
          {value ? value.toFixed(2) : "..."}
        </MDTypography>

        <MDBox
            width="20px"
            height="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              borderRadius: "50%",
              background:
                  trend === "up"
                      ? "rgba(22,163,74,0.12)"
                      : trend === "down"
                          ? "rgba(220,38,38,0.12)"
                          : darkMode
                              ? "rgba(148,163,184,0.12)"
                              : "rgba(148,163,184,0.16)",
            }}
        >
          <Icon
              sx={{
                color: getTrendColor(trend),
                fontSize: "18px !important",
                lineHeight: 1,
              }}
          >
            {getTrendIcon(trend)}
          </Icon>
        </MDBox>
      </MDBox>
  );

  const renderWeatherChip = () => (
      <Tooltip
          title={
            weather
                ? `${selectedClock.city} weather: ${weatherMeta.label}`
                : "Weather loading"
          }
          placement="bottom"
      >
        <MDBox
            display="flex"
            alignItems="center"
            gap={0.7}
            px={1.25}
            py={0.75}
            sx={{
              ...glassCardSx,
              whiteSpace: "nowrap",
              minHeight: "34px",
              transition: "all 0.25s ease",
              background: darkMode
                  ? "rgba(255,255,255,0.08)"
                  : "linear-gradient(135deg, rgba(239,246,255,0.95), rgba(255,255,255,0.9))",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: darkMode
                    ? "0 10px 30px rgba(0,0,0,0.32)"
                    : "0 10px 30px rgba(15,23,42,0.11)",
              },
            }}
        >
          <Icon
              sx={{
                fontSize: "18px !important",
                color: darkMode ? "#facc15" : "#2563eb",
              }}
          >
            {weatherMeta.icon}
          </Icon>

          <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{ color: darkMode ? "#fff" : "#334155" }}
          >
            {weather ? `${Math.round(weather.temperature)}°C` : "..."}
          </MDTypography>

          <MDTypography
              variant="caption"
              fontWeight="medium"
              sx={{
                color: darkMode ? "#d1d5db" : "#64748b",
                display: { xs: "none", xl: "block" },
              }}
          >
            {weatherMeta.label}
          </MDTypography>
        </MDBox>
      </Tooltip>
  );

  const renderWorldClockMenu = () => (
      <Menu
          anchorEl={worldClockAnchor}
          open={isWorldClockOpen}
          onClose={handleCloseWorldClock}
          PaperProps={{
            sx: {
              mt: 1.5,
              width: 370,
              borderRadius: "22px",
              overflow: "hidden",
              background: darkMode
                  ? "rgba(15,23,42,0.98)"
                  : "rgba(255,255,255,0.98)",
              border: darkMode
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "1px solid rgba(226,232,240,0.95)",
              boxShadow: darkMode
                  ? "0 26px 70px rgba(0,0,0,0.48)"
                  : "0 26px 70px rgba(15,23,42,0.16)",
            },
          }}
      >
        <MDBox px={2.4} py={2.1}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox>
              <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{ color: darkMode ? "#fff" : "#111827" }}
              >
                Global Operation Clock
              </MDTypography>

              <MDTypography
                  display="block"
                  variant="caption"
                  sx={{ color: darkMode ? "#94a3b8" : "#94a3b8" }}
              >
                Select city to update time and weather
              </MDTypography>
            </MDBox>

            <MDBox
                width="38px"
                height="38px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  borderRadius: "12px",
                  background: darkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
                  border: darkMode
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "1px solid rgba(226,232,240,0.9)",
                }}
            >
              <Icon sx={{ color: darkMode ? "#fff" : "#334155" }}>public</Icon>
            </MDBox>
          </MDBox>
        </MDBox>

        <Divider sx={{ my: 0 }} />

        <MDBox px={1.4} py={1.2}>
          {WORLD_CLOCKS.map((item) => {
            const isSelected = selectedClock.city === item.city;

            return (
                <MDBox
                    key={item.city}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1.2}
                    py={1.05}
                    mb={0.6}
                    onClick={() => {
                      setSelectedClock(item);
                      handleCloseWorldClock();
                    }}
                    sx={{
                      borderRadius: "14px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      background: isSelected
                          ? darkMode
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(241,245,249,1)"
                          : "transparent",
                      "&:hover": {
                        background: darkMode
                            ? "rgba(255,255,255,0.07)"
                            : "rgba(241,245,249,0.9)",
                      },
                    }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.2}>
                    <MDBox
                        width="36px"
                        height="36px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          borderRadius: "12px",
                          background: darkMode ? "rgba(255,255,255,0.08)" : "#f8fafc",
                          color: darkMode ? "#e5e7eb" : "#475569",
                          fontSize: "11px",
                          fontWeight: 700,
                          border: darkMode
                              ? "1px solid rgba(255,255,255,0.08)"
                              : "1px solid rgba(226,232,240,0.9)",
                        }}
                    >
                      {item.code}
                    </MDBox>

                    <MDBox>
                      <MDTypography
                          variant="button"
                          fontWeight="bold"
                          sx={{ color: darkMode ? "#fff" : "#1f2937" }}
                      >
                        {item.city}
                      </MDTypography>

                      <MDTypography
                          display="block"
                          variant="caption"
                          sx={{ color: darkMode ? "#94a3b8" : "#94a3b8" }}
                      >
                        {item.country}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDTypography
                      variant="button"
                      fontWeight="bold"
                      sx={{
                        color: darkMode ? "#fff" : "#111827",
                        letterSpacing: "0.2px",
                      }}
                  >
                    {formatWorldTime(item.timeZone)}
                  </MDTypography>
                </MDBox>
            );
          })}
        </MDBox>
      </Menu>
  );

  return (
      <AppBar
          position={absolute ? "absolute" : navbarType}
          color="inherit"
          sx={(theme) =>
              navbar(theme, { transparentNavbar, absolute, light, darkMode })
          }
      >
        <Toolbar
            sx={(theme) => ({
              ...navbarContainer(theme),
              gap: 2,
            })}
        >
          <MDBox
              color="inherit"
              mb={{ xs: 1, md: 0 }}
              sx={(theme) => navbarRow(theme, { isMini })}
          >
            <Breadcrumbs
                icon="home"
                title={breadcrumbTitle ?? route[route.length - 1]}
                route={route}
                light={light}
            />

            <IconButton
                sx={navbarDesktopMenu}
                onClick={handleMiniSidenav}
                size="small"
                disableRipple
            >
              <Icon fontSize="medium" sx={iconsStyle}>
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
          </MDBox>

          {!isMini && (
              <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={1.2}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
              >
                <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      display: { xs: "none", lg: "flex" },
                    }}
                >
                  {renderCurrencyItem({
                    flag: usdFlag,
                    code: "USD",
                    value: usdToTry,
                    trend: usdTrend,
                  })}

                  {renderCurrencyItem({
                    flag: eurFlag,
                    code: "EUR",
                    value: eurToTry,
                    trend: eurTrend,
                  })}

                  {renderCurrencyItem({
                    flag: gbpFlag,
                    code: "GBP",
                    value: gbpToTry,
                    trend: gbpTrend,
                  })}
                </MDBox>

                <Tooltip title="Select global city" placement="bottom">
                  <MDBox
                      display="flex"
                      alignItems="center"
                      gap={0.9}
                      px={1.4}
                      py={0.8}
                      onClick={handleOpenWorldClock}
                      sx={{
                        ...glassCardSx,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: darkMode
                              ? "0 10px 30px rgba(0,0,0,0.32)"
                              : "0 10px 30px rgba(15,23,42,0.11)",
                        },
                      }}
                  >
                    <Icon
                        sx={{
                          fontSize: "18px !important",
                          color: darkMode ? "#fff" : "#334155",
                        }}
                    >
                      public
                    </Icon>

                    <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                          color: darkMode ? "#fff" : "#334155",
                          display: { xs: "none", sm: "block" },
                        }}
                    >
                      Global Clock
                    </MDTypography>

                    <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          color: darkMode ? "#d1d5db" : "#64748b",
                          display: { xs: "none", xl: "block" },
                        }}
                    >
                      {selectedClock.city} {formatWorldTime(selectedClock.timeZone)}
                    </MDTypography>
                  </MDBox>
                </Tooltip>

                {renderWeatherChip()}

                <MDBox
                    display="flex"
                    alignItems="center"
                    gap={0.3}
                    px={0.6}
                    py={0.4}
                    sx={{
                      ...glassCardSx,
                      display: { xs: "none", xl: "flex" },
                    }}
                >
                  <IconButton
                      onClick={handleConfiguratorOpen}
                      size="small"
                      disableRipple
                  >
                    <Icon sx={{ fontSize: "20px !important", ...iconsStyle }}>settings</Icon>
                  </IconButton>

                  <IconButton
                      onClick={handleOpenMenu}
                      size="small"
                      disableRipple
                  >
                    <Badge badgeContent={unreadCount} color="error" max={99}>
                      <Icon sx={{ fontSize: "20px !important", ...iconsStyle }}>notifications</Icon>
                    </Badge>
                  </IconButton>
                </MDBox>
              </MDBox>
          )}
        </Toolbar>

        {renderMenu()}
        {renderWorldClockMenu()}
      </AppBar>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  breadcrumbTitle: PropTypes.string,
};

export default DashboardNavbar;